<template>
  <div :class="$style.container">
    <EnableSystemInsightsCTA
      v-if="isSystemInsightsSupported"
      additionalText="to access highlights for this device."
      :item="item"
    />

    <DeviceOverviewWidget
      :item="item"
    />

    <DeviceUsersWidget
      :users="users"
      @navigationMethod="(Tab, filter) => navigateToAsideTab(Tab, filter)"
    />

    <div :class="$style.grid">
      <DeviceGroupsWidget
        :groups="groups"
        @navigateToFullList="navigateToAsideTab(navigation.deviceGroups)"
        @navigationMethod="navigateToAsideTab(navigation.deviceGroups)"
      />
      <DevicePoliciesWidget
        :policies="policies"
        @navigateToFullList="navigateToAsideTab(navigation.policies)"
        @navigationMethod="navigateToAsideTab(navigation.policies)"
      />
    </div>

    <DeviceEventSummaryWidget
      :itemId="itemId"
      :systemDetails="item"
      @navigateToFullList="handleActivityLogLinkClick"
      @navigationMethod="(Tab, filter) => navigateToAsideTab(Tab, filter)"
    />
  </div>
</template>

<script>
import {
  DeviceConsts, ItemTypeConsts, ItemTypeRoutes, StoreModelMap, StoreModuleMap,
} from 'client/util/Constants';
import { eventFields } from 'client/Insights/ActivityLog/constants/EventMetadata';
import { mapActions, mapGetters, mapState } from 'vuex';
import { quickPicksOptions } from 'client/Insights/ActivityLog/constants/QuickPicksConsts';
import DeviceEventSummaryWidget from 'client/systems/DeviceHighlights/components/DeviceEventSummaryWidget';
import DeviceGroupsWidget from 'client/systems/DeviceHighlights/components/DeviceGroupsWidget';
import DeviceOverviewWidget from 'client/systems/DeviceHighlights/components/DeviceOverviewWidget';
import DevicePoliciesWidget from 'client/systems/DeviceHighlights/components/DevicePoliciesWidget';
import DeviceUsersWidget from 'client/systems/DeviceHighlights/components/DeviceUsersWidget';
import EnableSystemInsightsCTA from 'client/systems/DeviceHighlights/components/EnableSystemInsightsCTA';
import InsightsAccessMixin from 'client/Insights/InsightsAccessMixin';
import PolicyApi from 'client/api/v2/PolicyApi';
import SystemApi from 'client/api/v2/SystemApi';
import SystemGroupApi from 'client/api/v2/SystemGroupApi';
import SystemInsightsApi from 'client/api/v2/SystemInsightsApi';
import Util from 'client/util/Util';

const insightsEventModule = StoreModuleMap[ItemTypeConsts.insightsEvent];
const settingsModule = StoreModuleMap[ItemTypeConsts.settings];
const systemModel = StoreModelMap[ItemTypeConsts.system];
export default {
  name: 'DeviceHighlights',

  components: {
    DeviceEventSummaryWidget,
    DeviceUsersWidget,
    DeviceOverviewWidget,
    DevicePoliciesWidget,
    DeviceGroupsWidget,
    EnableSystemInsightsCTA,
  },

  mixins: [InsightsAccessMixin],

  props: {
    itemId: {
      type: String,
      default: '',
    },
    navigateToAsideTab: {
      type: Function,
      default: () => {},
    },
    viewport: {
      default: null,
      type: Object,
    },
  },

  data: () => ({
    navigation: DeviceConsts.navigation,
    groups: {
      hasError: false,
      isLoading: true,
      resources: [],
      totalCount: undefined,
    },
    policies: {
      hasError: false,
      isLoading: true,
      resources: [],
      totalCount: undefined,
    },
    users: {
      isLoading: true,
      resources: [],
      bound: {
        results: [],
        totalCount: 0,
      },
      unmanaged: {
        results: [],
        totalCount: 0,
      },
      totalCount: 0,
    },
  }),

  computed: {
    ...mapGetters(settingsModule, ['hasSystemInsights']),
    ...mapGetters(systemModel, ['isSystemInsightsSupported']),
    ...mapState(systemModel, ['item']),
  },

  async created() {
    this.fetchDeviceGroups();
    this.fetchDevicePolicies();
    this.fetchUsers();
  },

  methods: {
    ...mapActions(systemModel, ['setItemField']),
    ...mapActions(insightsEventModule, [
      'clearFilters',
      'setFilter',
      'setQuickPickDate',
    ]),

    async fetchDeviceGroups() {
      try {
        const { results, totalCount } = await SystemApi.getBoundSystemGroups(this.itemId, {
          limit: 5,
        });
        this.groups.totalCount = totalCount;

        if (totalCount) {
          const groupIds = results.map(d => d.id);
          const data = await Promise.all(groupIds.map(id => SystemGroupApi.get(id)));
          this.groups.resources = data.map(d => ({
            name: d.name,
            href: `${ItemTypeRoutes[ItemTypeConsts.systemGroup]}/${d.id}/details`,
          }));
        }
      } catch {
        this.groups.hasError = true;
      } finally {
        this.groups.isLoading = false;
      }
    },

    async fetchDevicePolicies() {
      try {
        const { results, totalCount } = await SystemApi.getBoundPolicies(this.itemId, {
          limit: 5,
        });
        this.policies.totalCount = totalCount;

        if (totalCount) {
          const policyIds = results.map(d => d.id);
          const data = await Promise.all(policyIds.map(id => PolicyApi.get(id)));
          this.policies.resources = data.map(d => ({
            name: d.name,
            href: `${ItemTypeRoutes[ItemTypeConsts.configuration]}/${d.id}/details`,
          }));
        }
      } catch {
        this.policies.hasError = true;
      } finally {
        this.policies.isLoading = false;
      }
    },

    async fetchSIUsersIfNeeded(params) {
      if (this.hasSystemInsights && this.item.systemInsights.state === 'enabled') {
        return SystemInsightsApi.getSystemInsightsUsers(params);
      }
      return Promise.resolve();
    },

    async fetchUsers() {
      const boundUserProps = { details: 'v1', limit: 3 };
      const unmanagedUsersProps = { limit: 10, filter: ['real_user:eq:true', 'managed:eq:false', `system_id:eq:${this.itemId}`] };
      const [bound, unmanaged] = await Promise.allSettled([
        SystemApi.getBoundUsers(this.itemId, boundUserProps),
        this.fetchSIUsersIfNeeded(unmanagedUsersProps),
      ]);

      if (bound.value) {
        this.users.bound = bound.value;
        this.users.totalCount += this.users.bound.totalCount;
      }

      if (unmanaged.value) {
        this.users.unmanaged = unmanaged.value;
        this.users.totalCount += this.users.unmanaged.totalCount;
      }

      this.users.isLoading = false;
    },

    handleActivityLogLinkClick() {
      // Clear any existing Activity Log filters
      this.clearFilters();

      // Set the time range to the first date available (15 or 90 days ago)
      const date = this.directoryInsightsPremium
        ? quickPicksOptions.ninetyDays
        : quickPicksOptions.fifteenDays;
      this.setQuickPickDate(date);

      const { displayName, hostname } = this.item;
      // Set the "System" filter for the Activity Log
      this.setFilter({
        field: eventFields.system,
        value: [displayName.toLowerCase(), hostname.toLowerCase()],
      });

      Util.redirect('#/insights');
    },
  },
};
</script>

<style module>
@import '../../components/Highlights/css/highlights.css';
</style>
